import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { getSupabaseBrowserClient } from "@/lib/supabase";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { MapChangeSnapshot, MapDocument } from "@west-marches/shared";
import type { Hex } from "@west-marches/shared";
import {
  REALTIME_EVENTS,
  presenceColorFromUserId,
  type MapChangeEvent,
  type PartyMoveEvent,
  type MapNoteChangeEvent,
  type MapNoteChangeKind,
  type RealtimePresenceState,
} from "@west-marches/shared";
import type { CampaignRole } from "@west-marches/shared";

export interface RealtimeUser {
  userId: string;
  displayName: string;
  role: CampaignRole;
  color: string;
}

export interface UseCampaignRealtimeChannelOpts {
  campaignId: string | null;
  userId: string | null;
  displayName: string | null;
  myRole: CampaignRole | null;
  canEdit: boolean;
  onRemoteMapChange: (snapshot: MapChangeSnapshot) => void;
  onRemotePartyMove: (target: Hex) => void;
  onRemoteDocumentUpdate?: (document: MapDocument) => void;
  onRemoteMapNoteChange?: (mapNoteId: string, kind: MapNoteChangeKind) => void;
}

export interface UseCampaignRealtimeChannelResult {
  onlineUsers: RealtimeUser[];
  broadcastMapChange: (snapshot: MapChangeSnapshot) => void;
  broadcastPartyMove: (target: Hex) => void;
  broadcastMapNoteChange: (mapNoteId: string, kind: MapNoteChangeKind) => void;
}

const NOOP_RESULT: UseCampaignRealtimeChannelResult = {
  onlineUsers: [],
  broadcastMapChange: () => {},
  broadcastPartyMove: () => {},
  broadcastMapNoteChange: () => {},
};

export function useCampaignRealtimeChannel(
  opts: UseCampaignRealtimeChannelOpts,
): UseCampaignRealtimeChannelResult {
  const {
    campaignId,
    userId,
    displayName,
    myRole,
    canEdit,
    onRemoteMapChange,
    onRemotePartyMove,
    onRemoteDocumentUpdate,
    onRemoteMapNoteChange,
  } = opts;

  const [onlineUsers, setOnlineUsers] = useState<RealtimeUser[]>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);

  const onRemoteMapChangeRef = useRef(onRemoteMapChange);
  onRemoteMapChangeRef.current = onRemoteMapChange;
  const onRemotePartyMoveRef = useRef(onRemotePartyMove);
  onRemotePartyMoveRef.current = onRemotePartyMove;
  const onRemoteDocumentUpdateRef = useRef(onRemoteDocumentUpdate);
  onRemoteDocumentUpdateRef.current = onRemoteDocumentUpdate;
  const onRemoteMapNoteChangeRef = useRef(onRemoteMapNoteChange);
  onRemoteMapNoteChangeRef.current = onRemoteMapNoteChange;

  useEffect(() => {
    if (!campaignId || !userId) {
      return;
    }

    const supabase = getSupabaseBrowserClient();
    const channelName = `campaign:${campaignId}`;

    const channel = supabase.channel(channelName, {
      config: { broadcast: { self: false }, presence: { key: userId }, private: true },
    });

    channel
      .on("broadcast", { event: REALTIME_EVENTS.MAP_CHANGE }, (payload) => {
        const data = payload.payload as MapChangeEvent;
        if (data.senderId !== userId) {
          onRemoteMapChangeRef.current(data.snapshot);
        }
      })
      .on("broadcast", { event: REALTIME_EVENTS.PARTY_MOVE }, (payload) => {
        const data = payload.payload as PartyMoveEvent;
        if (data.senderId !== userId) {
          onRemotePartyMoveRef.current(data.target);
        }
      })
      .on("broadcast", { event: REALTIME_EVENTS.MAP_NOTE_CHANGE }, (payload) => {
        const data = payload.payload as MapNoteChangeEvent;
        if (data.senderId !== userId) {
          onRemoteMapNoteChangeRef.current?.(data.mapNoteId, data.kind);
        }
      })
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState<RealtimePresenceState>();
        const users: RealtimeUser[] = [];
        for (const presences of Object.values(state)) {
          const presence = presences[0];
          if (presence) {
            users.push({
              userId: presence.userId,
              displayName: presence.displayName,
              role: presence.role,
              color: presence.color,
            });
          }
        }
        setOnlineUsers(users);
      })
      .subscribe(async (status, err) => {
        if (status === "SUBSCRIBED") {
          Sentry.logger.info("Realtime channel subscribed: %s", [channelName]);
          await channel.track({
            userId,
            displayName: displayName ?? "Unknown",
            role: myRole ?? "player",
            color: presenceColorFromUserId(userId),
            joinedAt: new Date().toISOString(),
          } satisfies RealtimePresenceState);
        } else {
          Sentry.logger.warn("Realtime channel %s status: %s %s", [
            channelName,
            status,
            err?.message ?? "",
          ]);
        }
      });

    channelRef.current = channel;

    return () => {
      channelRef.current = null;
      void supabase.removeChannel(channel);
    };
  }, [campaignId, userId, displayName, myRole]);

  useEffect(() => {
    if (!campaignId || !userId) {
      return;
    }

    const supabase = getSupabaseBrowserClient();
    const dbChannelName = `campaign-db:${campaignId}`;

    const dbChannel = supabase
      .channel(dbChannelName)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "campaigns",
          filter: `id=eq.${campaignId}`,
        },
        (payload) => {
          const newRow = payload.new as { document?: unknown; owner_id?: string };
          if (!newRow.document || !onRemoteDocumentUpdateRef.current) {
            return;
          }
          try {
            const doc = newRow.document as MapDocument;
            onRemoteDocumentUpdateRef.current(doc);
          } catch (error) {
            Sentry.logger.warn("Failed to process postgres_changes document update: %s", [
              error instanceof Error ? error.message : String(error),
            ]);
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(dbChannel);
    };
  }, [campaignId, userId]);

  const broadcastMapChange = useCallback(
    (snapshot: MapChangeSnapshot) => {
      if (!canEdit || !userId) return;
      const channel = channelRef.current;
      if (!channel) return;
      channel
        .send({
          type: "broadcast",
          event: REALTIME_EVENTS.MAP_CHANGE,
          payload: { senderId: userId, snapshot } satisfies MapChangeEvent,
        })
        .then((status) => {
          if (status !== "ok") {
            Sentry.logger.warn("Broadcast map:change returned: %s", [status]);
          }
        });
    },
    [canEdit, userId],
  );

  const broadcastPartyMove = useCallback(
    (target: Hex) => {
      if (!canEdit || !userId) return;
      const channel = channelRef.current;
      if (!channel) return;
      channel
        .send({
          type: "broadcast",
          event: REALTIME_EVENTS.PARTY_MOVE,
          payload: { senderId: userId, target } satisfies PartyMoveEvent,
        })
        .then((status) => {
          if (status !== "ok") {
            Sentry.logger.warn("Broadcast party:move returned: %s", [status]);
          }
        });
    },
    [canEdit, userId],
  );

  const broadcastMapNoteChange = useCallback(
    (mapNoteId: string, kind: MapNoteChangeKind) => {
      if (!canEdit || !userId) return;
      const channel = channelRef.current;
      if (!channel) return;
      channel
        .send({
          type: "broadcast",
          event: REALTIME_EVENTS.MAP_NOTE_CHANGE,
          payload: { senderId: userId, mapNoteId, kind } satisfies MapNoteChangeEvent,
        })
        .then((status) => {
          if (status !== "ok") {
            Sentry.logger.warn("Broadcast map-note:change returned: %s", [status]);
          }
        });
    },
    [canEdit, userId],
  );

  if (!campaignId || !userId) {
    return NOOP_RESULT;
  }

  return { onlineUsers, broadcastMapChange, broadcastPartyMove, broadcastMapNoteChange };
}
